import React, { useState, useEffect, useCallback } from 'react';
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/react';
import toast from 'react-hot-toast';
import { masterDataApi } from '../services/api';
import { MasterDataValue } from '../types';

interface MasterDataComboboxProps {
  categoryName: string;
  value: string;
  onChange: (value: string) => void;
  allowCreate?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

const MasterDataCombobox: React.FC<MasterDataComboboxProps> = ({
  categoryName,
  value,
  onChange,
  allowCreate = true,
  placeholder,
  disabled = false,
  required = false,
}) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<MasterDataValue[]>([]);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await masterDataApi.getCategory(categoryName);
        if (!cancelled) {
          setCategoryId(response.id);
          setOptions((response.values ?? []).filter((v) => v.is_active));
        }
      } catch {
        if (!cancelled) {
          setCategoryId(null);
          setOptions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [categoryName]);

  const filtered =
    query === ''
      ? options
      : options.filter(
          (opt) =>
            opt.value.toLowerCase().includes(query.toLowerCase()) ||
            (opt.display_value &&
              opt.display_value.toLowerCase().includes(query.toLowerCase()))
        );

  const exactMatch = options.some(
    (opt) => opt.value.toLowerCase() === query.trim().toLowerCase()
  );
  const showAddOption =
    allowCreate && categoryId !== null && query.trim() !== '' && !exactMatch && !creating;

  const handleCreate = useCallback(
    async (newValue: string) => {
      if (!categoryId || !newValue.trim() || creating) return;
      setCreating(true);
      try {
        const created = await masterDataApi.createValue(categoryId, {
          key: newValue.trim().toLowerCase().replace(/\s+/g, '_'),
          value: newValue.trim(),
          display_value: newValue.trim(),
          sort_order: 0,
          is_active: true,
        } as Partial<MasterDataValue>);
        setOptions((prev) => [...prev, created]);
        toast.success(`„${created.value}" hozzáadva`);
      } catch {
        toast.error('Hiba történt a hozzáadás során');
      } finally {
        setCreating(false);
      }
    },
    [categoryId, creating]
  );

  const handleChange = (selectedValue: string | null) => {
    if (selectedValue === null) return;
    const exists = options.some((opt) => opt.value === selectedValue);
    onChange(selectedValue);
    if (!exists && allowCreate && categoryId) {
      handleCreate(selectedValue);
    }
  };

  const handleClear = () => {
    onChange('');
    setQuery('');
  };

  return (
    <Combobox value={value} onChange={handleChange} disabled={disabled} immediate>
      <div className="relative">
        <ComboboxInput
          className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          displayValue={(val: string) => val ?? ''}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          required={required}
        />
        {value && !disabled ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        ) : (
          <ComboboxButton className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </ComboboxButton>
        )}
        <ComboboxOptions
          anchor="bottom start"
          className="z-50 w-[var(--input-width)] rounded-md bg-white shadow-lg border border-gray-200 overflow-auto max-h-60 focus:outline-none empty:hidden"
        >
          {loading && (
            <div className="px-3 py-2 text-sm text-gray-400">Betöltés...</div>
          )}
          {!loading && filtered.length === 0 && !showAddOption && query.trim() !== '' && (
            <div className="px-3 py-2 text-sm text-gray-500">Nincs találat</div>
          )}
          {filtered.map((opt) => (
            <ComboboxOption
              key={opt.id}
              value={opt.value}
              className="px-3 py-2 cursor-pointer text-sm text-gray-700 select-none data-[focus]:bg-gray-50"
            >
              {opt.display_value || opt.value}
            </ComboboxOption>
          ))}
          {showAddOption && (
            <ComboboxOption
              value={query.trim()}
              className="px-3 py-2 cursor-pointer text-sm text-green-700 select-none data-[focus]:bg-green-50 border-t border-gray-100 font-medium"
            >
              + Hozzáadás: „{query.trim()}"
            </ComboboxOption>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};

export default MasterDataCombobox;
